/**
 * EVERY REACTION THAT IS OWED, AND WHOSE IT IS.
 *
 * The defence document has always been broadcast: one per combatant under
 * `sessions/{id}/defence/{combatantId}`, delivered to every client in the
 * session. `useDefenceSync` builds the whole map. Then the app threw all but
 * one entry away:
 *
 *     const fsDefence = getDefenceForTab(activeTab);
 *
 * `activeTab` is this browser's PRIVATE target pointer, so the prompt appeared
 * for whoever happened to be looking at the attacked combatant - which is
 * nobody in particular, and frequently nobody at all. That is the bug. It is a
 * visibility bug and not a permissions one: the roster is shared, anyone may
 * already act as anyone, and resolution is a first-writer-wins transaction, so
 * showing a reaction to everybody is safe by construction.
 *
 * THE VOLUME IS DECIDED LOCALLY, from state that already exists.
 *
 *   loud  - the attacked combatant is the one I am acting as, or (with the
 *           table's ruling) I am the GM
 *   calm  - everybody else, who still sees it and may still answer
 *
 * No ownership model, nothing synced, nothing to configure per character.
 *
 * This hook is also where the reaction *chain* will live once several can be
 * outstanding at once, which is why it owns the focused id rather than letting
 * three separate call sites reach for `activeTab`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_REACTION_ALARM } from '../engine/reactionAlarm';
import type { ReactionAlarmPolicy } from '../engine/reactionAlarm';
import { listOwed, pickFocused, type OwedReaction } from '../engine/reactionQueue';

export interface UseReactionQueueParams {
  /** Every pending defence in the session, keyed by combatant id. */
  pendingDefences: Map<string, Record<string, unknown>> | null | undefined;
  /** The attacker's own not-yet-shared attack, for solo and pre-sync play. */
  pendingAttack: { tabId?: string } | null;
  /** Fallback target for the local pending attack. */
  currentTab: { target?: unknown } | null | undefined;
  roster: { id: string; name: string }[];
  /** Who this browser is acting as. The only input to "is this mine". */
  actorId: string | null;
  /**
   * Is this combatant controlled by my actor?
   *
   * Injected rather than computed here, because answering it needs the whole
   * roster of Combatants and this hook is given only `{id, name}`. The
   * predicate is `isControlledBy` from `engine/roster/control.ts`, bound to
   * the roster by the caller.
   */
  isControlledByActor?: (combatantId: string) => boolean;
  isGm: boolean;
  sessionId: string | null;
  /** This browser's seat, for spotting the reactions THIS client is waiting on. */
  playerId?: string | null;
  /** The table's ruling on who hears the alarm. See engine/reactionAlarm.ts. */
  alarmPolicy?: ReactionAlarmPolicy;
  /**
   * The combatant whose defence chain is mid-flight, if any.
   *
   * A chain is a modal back-and-forth held in ONE slot
   * (`useDefenceResolution`), so while one is running the prompt must not be
   * pointed anywhere else - answering a second reaction would overwrite the
   * first one's completed steps. Rather than making the chain a map of
   * chains, which nobody can use (you cannot dodge two attacks at once), the
   * queue simply pins focus for the duration.
   */
  chainTabId?: string | null;
  /** How long a reaction lives, for the countdown. */
  timeoutMs?: number;
  /** Injected so the countdown is testable without waiting two minutes. */
  now?: number;
}

export interface UseReactionQueueReturn {
  /** Everything owed right now, loudest first. */
  owed: OwedReaction[];
  /** The one the prompt is answering, if any. */
  focused: OwedReaction | null;
  focusedId: string | null;
  /** Everything owed except the focused one, in the same order. */
  queued: OwedReaction[];
  /**
   * The reactions this client's own attack is waiting on.
   *
   * Disjoint from anything you can answer: it is the other end of the same
   * document. Before this, an attacker who fired and then saw nothing happen
   * had no way to tell a slow table from a lost write.
   */
  awaiting: OwedReaction[];
  /** How many are waiting behind the focused one. */
  waiting: number;
  /** True while a defence chain pins the prompt in place. */
  locked: boolean;
  /** Point the prompt at a specific combatant. */
  focus: (combatantId: string) => void;
}

/** Everything owed right now, recomputed when any input changes. */
function useOwed({
  pendingDefences,
  pendingAttack,
  currentTab,
  roster,
  actorId,
  isGm,
  alarmPolicy = DEFAULT_REACTION_ALARM,
  isControlledByActor,
  timeoutMs,
  now,
}: UseReactionQueueParams): OwedReaction[] {
  return useMemo<OwedReaction[]>(() => {
    const listener = { actorId, isGm, alarmPolicy, isControlledByActor };
    const clock = now ?? Date.now();
    return listOwed({ pendingDefences, pendingAttack, currentTab, roster, listener, timeoutMs, clock });
  }, [
    pendingDefences,
    pendingAttack,
    currentTab,
    roster,
    actorId,
    isGm,
    alarmPolicy,
    isControlledByActor,
    timeoutMs,
    now,
  ]);
}

/**
 * Which one the prompt answers. The request is null for "the first owed",
 * so a new reaction surfaces without anybody choosing it.
 */
function useFocus(owed: OwedReaction[], chainTabId: string | null | undefined) {
  const [focusRequest, setFocusRequest] = useState<string | null>(null);
  // A live chain wins over everything: see `chainTabId`.
  const focused = useMemo(() => pickFocused(owed, chainTabId, focusRequest), [owed, focusRequest, chainTabId]);
  // NEXT ONE, ON ANSWER. When the reaction you were answering leaves the queue
  // the request that pointed at it is stale, and holding it would make a later
  // reaction for the SAME combatant silently jump the queue.
  useEffect(() => {
    if (focusRequest && !owed.some((o) => o.combatantId === focusRequest)) setFocusRequest(null);
  }, [owed, focusRequest]);
  const focus = useCallback((combatantId: string) => setFocusRequest(combatantId), []);
  return { focused, focusedId: focused?.combatantId ?? null, focus };
}

export default function useReactionQueue(params: UseReactionQueueParams): UseReactionQueueReturn {
  const { playerId, chainTabId } = params;
  const owed = useOwed(params);
  const { focused, focusedId, focus } = useFocus(owed, chainTabId);
  const queued = useMemo(() => owed.filter((o) => o.combatantId !== focusedId), [owed, focusedId]);
  // WHO I AM WAITING ON. `attackerId` is the seat that fired, which is the
  // same field the app already uses to route an attacker's follow-up prompts.
  const awaiting = useMemo(
    () => (playerId ? owed.filter((o) => o.doc?.attackerId === playerId) : []),
    [owed, playerId],
  );
  // One answer per change of what is owed (plan shell-contexts, A3): a new object each render gave the combat-flow
  // context a new value with every one.
  const locked = !!(chainTabId && focusedId === chainTabId);
  return useMemo(
    () => ({ owed, focused, focusedId, queued, awaiting, waiting: queued.length, locked, focus }),
    [owed, focused, focusedId, queued, awaiting, locked, focus],
  );
}
